import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { dbApi, getApiErrorMessage } from "@/lib/api"
import { submitChatFeedback } from "@/lib/chatFeedback"
import { AnalyticsEvents, trackEvent } from "@/lib/analytics"
import type {
  ChatHistoryItem,
  ChatMessage,
  ChatHistoryDetails,
  PendingNewChat,
  MessageFeedback,
} from "@/types/chatHistory"
import { sortChatsNewestFirst, deleteChat as deleteChatApi } from "@/lib/chats"
import { deriveChatFromJurisdiction } from "@/lib/chatJurisdiction"
import { filterHttpSources } from "@/lib/sources"
import { requestDeepDive as fetchDeepDive } from "@/lib/deepDiveApi"
import type { DttJurisdiction } from "@/types/jurisdictions"
import type { DefinitionCard } from "@/types/definitions"

interface ChatHistoryContextValue {
  chats: ChatHistoryItem[]
  pendingNewChat: PendingNewChat | null
  currentChatId: string | null
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  loadHistory: () => Promise<void>
  selectChat: (chatId: string) => Promise<void>
  /** Re-attach the view to the streaming new-chat placeholder in the sidebar. */
  selectPendingChat: () => void
  createNewChat: () => void
  deleteChat: (chatId: string) => Promise<boolean>
  addUserMessage: (userMsg: string) => void
  /** Begins a streaming turn; returns an AbortSignal that fires only if the
   * turn's chat is deleted while it is still in flight. */
  startAssistantMessage: () => AbortSignal
  appendAssistantToken: (token: string) => void
  /** Appends a chain-of-thought delta to the in-flight assistant message (dev/local). */
  appendAssistantThinking: (token: string) => void
  setAssistantStatus: (text: string) => void
  finalizeAssistantMessage: (
    assistantTimestamp?: string,
    sources?: string[],
    definitions?: DefinitionCard[]
  ) => void
  /** Drops the in-flight turn; returns true if that turn's chat was on screen
   * (so the caller knows whether to surface an error to the user). */
  cancelAssistantMessage: () => boolean
  addAssistantMessage: (
    assistantMsg: string,
    assistantTimestamp?: string,
    sources?: string[]
  ) => void
  submitFeedback: (
    feedback: MessageFeedback,
    assistantTimestamp?: string
  ) => Promise<boolean>
  /** Generate the deep-dive message for a finalized assistant answer on demand (Thread H).
   * Resolves true if a deep dive was produced. */
  requestDeepDive: (assistantTimestamp?: string) => Promise<boolean>
  /** Whether a deep-dive request for this message (in the chat on screen) is in flight. */
  isDeepDivePending: (assistantTimestamp?: string) => boolean
  /** Latest progress line for an in-flight deep dive (CEO-1), e.g. "Scanning the legislation… (step 2 of 5)". */
  deepDiveStatus: (assistantTimestamp?: string) => string | undefined
  /** 08-24: try-again message for a message whose last dive attempt failed. */
  deepDiveError: (assistantTimestamp?: string) => string | undefined
  setChatId: (id: string) => void
  setPendingNewChatTitle: (title: string) => void
  setPendingChatId: (id: string) => void
  /** Increments each time the user starts a new chat (sidebar "New chat"). */
  newChatVersion: number
  /** Treaty counterpart restored from the selected chat's history, or null. */
  chatFromJurisdiction: DttJurisdiction | null
  /** Bumps when the user picks a chat from the sidebar (not on first send). */
  chatSelectionVersion: number
  /** Keep in-memory treaty scope aligned after a message is sent. */
  syncChatJurisdiction: (jurisdiction: DttJurisdiction | null) => void
}

const ChatHistoryContext = createContext<ChatHistoryContextValue | null>(null)

/** The one streaming turn currently in flight, if any. Held separately from
 * the displayed messages so the user can navigate to other chats and back
 * without losing it — the response keeps streaming in the background. */
interface StreamSession {
  /** The chat the turn belongs to, or null for a new chat the backend hasn't
   * assigned an id to yet (re-keyed once setChatId fires). */
  chatId: string | null
  /** For new chats (chatId null), the newChatVersion when the turn started.
   * Distinguishes "the new chat that's streaming" from a freshly opened one. */
  epoch?: number
  /** First-message label for a new chat, used to seed the sidebar entry once
   * the backend assigns this turn its chat id. */
  title?: string
  messages: ChatMessage[]
}

function lastAssistantIndex(msgs: ChatMessage[]): number {
  return msgs.reduce((found, m, i) => (m.role === "assistant" ? i : found), -1)
}

export function ChatHistoryProvider({ children }: { children: ReactNode }) {
  const [chats, setChats] = useState<ChatHistoryItem[]>([])
  const [pendingNewChat, setPendingNewChat] = useState<PendingNewChat | null>(
    null
  )
  const [currentChatId, setCurrentChatId] = useState<string | null>(null)
  // Messages loaded from the DB for the chat on screen. The in-flight turn is
  // overlaid on top of this via the derived `messages` below.
  const [dbMessages, setDbMessages] = useState<ChatMessage[]>([])
  const [inflight, setInflight] = useState<StreamSession | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newChatVersion, setNewChatVersion] = useState(0)
  const [chatFromJurisdiction, setChatFromJurisdiction] =
    useState<DttJurisdiction | null>(null)
  const [chatSelectionVersion, setChatSelectionVersion] = useState(0)
  // Keys (`${chatId}::${timestamp}`) of on-demand deep-dive requests in flight (Thread H).
  const [pendingDeepDives, setPendingDeepDives] = useState<Set<string>>(() => new Set())

  // Refs mirror the state above so the streaming callbacks (which capture a
  // single render via useCallback([])) always read the latest values.
  const currentChatIdRef = useRef<string | null>(null)
  const dbMessagesRef = useRef<ChatMessage[]>([])
  const inflightRef = useRef<StreamSession | null>(null)
  const streamingRef = useRef(false)
  const pendingDeepDivesRef = useRef<Set<string>>(new Set())
  const streamAbortRef = useRef<AbortController | null>(null)
  const newChatVersionRef = useRef(0)
  // Ids of new chats added to the sidebar optimistically (on the backend
  // assigning an id mid-stream) before the chat-list endpoint reports them.
  // loadHistory keeps these until the server catches up, then drops them.
  const optimisticChatIdsRef = useRef<Set<string>>(new Set())

  // Is this session's chat the one currently on screen? (ref-based, for the
  // streaming callbacks). New chats match by null id + matching epoch so a
  // freshly opened new chat doesn't adopt a still-streaming earlier one.
  const sessionOnScreen = useCallback((s: StreamSession | null): boolean => {
    if (!s) return false
    if (s.chatId !== null) return s.chatId === currentChatIdRef.current
    return (
      currentChatIdRef.current === null && s.epoch === newChatVersionRef.current
    )
  }, [])

  const updateCurrentChatId = useCallback((id: string | null) => {
    currentChatIdRef.current = id
    setCurrentChatId(id)
  }, [])

  const updateDbMessages = useCallback(
    (next: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      const value =
        typeof next === "function" ? next(dbMessagesRef.current) : next
      dbMessagesRef.current = value
      setDbMessages(value)
    },
    []
  )

  const writeInflight = useCallback((session: StreamSession | null) => {
    inflightRef.current = session
    streamingRef.current =
      session !== null && session.messages.some((m) => m.streaming)
    setInflight(session)
  }, [])

  // The in-flight turn is shown only while its chat is the one on screen;
  // otherwise it streams in the background and the screen shows DB messages.
  const inflightOnScreen =
    inflight !== null &&
    (inflight.chatId !== null
      ? inflight.chatId === currentChatId
      : currentChatId === null && inflight.epoch === newChatVersion)
  const messages = inflightOnScreen ? inflight.messages : dbMessages

  const loadHistory = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const { data } = await dbApi.get<ChatHistoryItem[]>("api/chats")
      const serverIds = new Set(data.map((c) => String(c.chat_id)))
      // Confirmed chats no longer need their optimistic placeholder.
      optimisticChatIdsRef.current = new Set(
        [...optimisticChatIdsRef.current].filter((id) => !serverIds.has(id))
      )
      // Merge: keep optimistically-added chats the server hasn't reported yet,
      // so a just-created chat doesn't blink out if the list endpoint lags.
      setChats((prev) => {
        const pendingOptimistic = prev.filter(
          (c) =>
            optimisticChatIdsRef.current.has(String(c.chat_id)) &&
            !serverIds.has(String(c.chat_id))
        )
        return sortChatsNewestFirst([...pendingOptimistic, ...data])
      })
      setPendingNewChat((prev) => {
        if (prev?.chat_id && serverIds.has(prev.chat_id)) return null
        return prev
      })
    } catch (e) {
      setError(getApiErrorMessage(e))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const selectChat = useCallback(
    async (chatId: string) => {
      // Keep a streaming turn alive in the background; only drop a finished one.
      // The pending placeholder is the new chat's only sidebar entry until
      // loadHistory runs at the end of its stream, so keep it visible while that
      // turn is still in flight — otherwise navigating away makes it vanish.
      if (!streamingRef.current) {
        writeInflight(null)
        setPendingNewChat(null)
      }
      setIsLoading(true)
      setError(null)
      try {
        const { data } = await dbApi.get<ChatHistoryDetails>(
          `api/chat/${chatId}`
        )
        const details = Array.isArray(data) ? data : []
        updateCurrentChatId(chatId)
        setChatFromJurisdiction(deriveChatFromJurisdiction(details))
        setChatSelectionVersion((v) => v + 1)
        const normalized: ChatMessage[] = details.map((m) => {
          const sources = filterHttpSources(m.sources)
          const deepDive = typeof m.deep_dive === "string" ? m.deep_dive : ""
          return {
            role: m.role,
            content: m.content,
            timestamp: m.timestamp,
            feedback: m.feedback,
            ...(sources.length ? { sources } : {}),
            ...(deepDive ? { deep_dive: deepDive } : {}),   // Thread H: persisted second bubble
            ...(m.sme_feedback ? { sme_feedback: m.sme_feedback } : {}),  // #53 dev-only SME review
          }
        })
        // If the turn in flight belongs to this chat, the derived `messages`
        // keeps showing the live session; these DB rows are the fallback for
        // once it finalizes.
        updateDbMessages(normalized)
        trackEvent(AnalyticsEvents.ChatSelected, {
          chat_id: chatId,
          message_count: normalized.length,
        })
      } catch (e) {
        setError(getApiErrorMessage(e))
      } finally {
        setIsLoading(false)
      }
    },
    [writeInflight, updateCurrentChatId, updateDbMessages]
  )

  const selectPendingChat = useCallback(() => {
    // The placeholder only shows in the pre-id window (before the backend
    // assigns this new chat an id and it graduates into the sidebar list).
    // Re-attach the view to the in-flight session; its null id + epoch makes
    // the derived `messages` show the live stream again.
    setError(null)
    const cur = inflightRef.current
    if (cur) updateCurrentChatId(cur.chatId)
  }, [updateCurrentChatId])

  const createNewChat = useCallback(() => {
    if (!streamingRef.current) writeInflight(null)
    updateCurrentChatId(null)
    updateDbMessages([])
    setChatFromJurisdiction(null)
    setError(null)
    setPendingNewChat({ title: "" })
    newChatVersionRef.current += 1
    setNewChatVersion(newChatVersionRef.current)
    trackEvent(AnalyticsEvents.NewChatStarted)
  }, [writeInflight, updateCurrentChatId, updateDbMessages])

  const deleteChat = useCallback(
    async (chatId: string) => {
      setError(null)
      try {
        await deleteChatApi(chatId)
        optimisticChatIdsRef.current.delete(chatId)
        setChats((prev) => prev.filter((c) => String(c.chat_id) !== chatId))
        // If the deleted chat owns the in-flight turn, abort it — there is
        // nowhere left for the response to land.
        const cur = inflightRef.current
        if (cur && cur.chatId !== null && String(cur.chatId) === chatId) {
          streamAbortRef.current?.abort()
          writeInflight(null)
        }
        if (String(currentChatId) === chatId) {
          updateCurrentChatId(null)
          updateDbMessages([])
          setChatFromJurisdiction(null)
          // Deleting the current chat already drops us into the empty new-chat
          // state; no "Untitled" placeholder entry needed in the sidebar.
          setPendingNewChat(null)
        }
        return true
      } catch (e) {
        setError(getApiErrorMessage(e))
        return false
      }
    },
    [currentChatId, writeInflight, updateCurrentChatId, updateDbMessages]
  )

  const addUserMessage = useCallback(
    (userMsg: string) => {
      // Open a new turn on top of whatever is on screen. A previous turn is
      // already folded into dbMessages by the time another can start (the send
      // UI is gated on the request finishing), so dbMessages is the base.
      const chatId = currentChatIdRef.current
      writeInflight({
        chatId,
        epoch: chatId === null ? newChatVersionRef.current : undefined,
        // A new chat's first message labels its sidebar entry once it gets an id.
        title: chatId === null ? userMsg.slice(0, 100) : undefined,
        messages: [...dbMessagesRef.current, { role: "user", content: userMsg }],
      })
    },
    [writeInflight]
  )

  const startAssistantMessage = useCallback((): AbortSignal => {
    const controller = new AbortController()
    streamAbortRef.current = controller
    const cur = inflightRef.current ?? {
      chatId: currentChatIdRef.current,
      epoch:
        currentChatIdRef.current === null
          ? newChatVersionRef.current
          : undefined,
      messages: dbMessagesRef.current,
    }
    writeInflight({
      chatId: cur.chatId,
      epoch: cur.epoch,
      title: cur.title,
      messages: [
        ...cur.messages,
        { role: "assistant", content: "", streaming: true },
      ],
    })
    return controller.signal
  }, [writeInflight])

  const appendAssistantToken = useCallback(
    (token: string) => {
      const cur = inflightRef.current
      if (!cur || !streamingRef.current) return
      const idx = lastAssistantIndex(cur.messages)
      if (idx < 0) return
      writeInflight({
        ...cur,
        messages: cur.messages.map((m, i) =>
          // First real token supersedes any progress status line.
          i === idx
            ? { ...m, content: m.content + token, status: undefined }
            : m
        ),
      })
    },
    [writeInflight]
  )

  const appendAssistantThinking = useCallback(
    (token: string) => {
      const cur = inflightRef.current
      if (!cur || !streamingRef.current) return
      const idx = lastAssistantIndex(cur.messages)
      if (idx < 0) return
      writeInflight({
        ...cur,
        messages: cur.messages.map((m, i) =>
          i === idx ? { ...m, thinking: (m.thinking ?? "") + token } : m
        ),
      })
    },
    [writeInflight]
  )

  const setAssistantStatus = useCallback(
    (text: string) => {
      const cur = inflightRef.current
      if (!cur || !streamingRef.current) return
      const idx = lastAssistantIndex(cur.messages)
      if (idx < 0) return
      writeInflight({
        ...cur,
        messages: cur.messages.map((m, i) =>
          i === idx ? { ...m, status: text } : m
        ),
      })
    },
    [writeInflight]
  )

  const finalizeAssistantMessage = useCallback(
    (
      assistantTimestamp?: string,
      sources?: string[],
      definitions?: DefinitionCard[]
    ) => {
      const cur = inflightRef.current
      if (!cur || !streamingRef.current) return
      const httpSources = filterHttpSources(sources)
      const idx = lastAssistantIndex(cur.messages)
      const finalMessages =
        idx < 0
          ? cur.messages
          : cur.messages.map((m, i) =>
              i === idx
                ? {
                    ...m,
                    streaming: false,
                    status: undefined,
                    ...(assistantTimestamp
                      ? { timestamp: assistantTimestamp }
                      : {}),
                    ...(httpSources.length ? { sources: httpSources } : {}),
                    ...(definitions?.length ? { definitions } : {}),
                  }
                : m
            )
      // Fold the completed turn into the chat's persisted view when it is on
      // screen; otherwise the DB (refetched on the next visit) is the source of
      // truth. Either way the session is done.
      if (sessionOnScreen(cur)) updateDbMessages(finalMessages)
      writeInflight(null)
    },
    [sessionOnScreen, writeInflight, updateDbMessages]
  )

  const cancelAssistantMessage = useCallback((): boolean => {
    const cur = inflightRef.current
    if (!cur) return false
    const onScreen = sessionOnScreen(cur)
    const idx = lastAssistantIndex(cur.messages)
    // Drop the assistant placeholder but keep the user's message visible.
    const trimmed =
      idx >= 0 && cur.messages[idx].streaming
        ? cur.messages.filter((_, i) => i !== idx)
        : cur.messages
    if (onScreen) updateDbMessages(trimmed)
    writeInflight(null)
    return onScreen
  }, [sessionOnScreen, writeInflight, updateDbMessages])

  const addAssistantMessage = useCallback(
    (assistantMsg: string, assistantTimestamp?: string, sources?: string[]) => {
      const httpSources = filterHttpSources(sources)
      updateDbMessages((prev) => [
        ...(prev ?? []),
        {
          role: "assistant",
          content: assistantMsg,
          ...(assistantTimestamp ? { timestamp: assistantTimestamp } : {}),
          ...(httpSources.length ? { sources: httpSources } : {}),
        },
      ])
    },
    [updateDbMessages]
  )

  const submitFeedback = useCallback(
    async (feedback: MessageFeedback, assistantTimestamp?: string) => {
      if (!currentChatId) return false
      const result = await submitChatFeedback(
        currentChatId,
        feedback,
        assistantTimestamp
      )
      if (!result.ok) {
        setError(result.error)
        return false
      }
      updateDbMessages((prev) => {
        const idx = lastAssistantIndex(prev)
        if (idx < 0) return prev
        const ts = assistantTimestamp ?? prev[idx].timestamp
        return prev.map((m, i) => {
          if (i !== idx) return m
          if (ts && m.timestamp && m.timestamp !== ts) return m
          return { ...m, feedback, ...(ts ? { timestamp: ts } : {}) }
        })
      })
      return true
    },
    [currentChatId, updateDbMessages]
  )

  // On-demand deep dive (Thread H) — keyed by `${chatId}::${timestamp}` so the loading
  // state survives the button unmounting when the user switches chats mid-request.
  const setPendingDeepDive = useCallback((key: string, on: boolean) => {
    const next = new Set(pendingDeepDivesRef.current)
    if (on) next.add(key)
    else next.delete(key)
    pendingDeepDivesRef.current = next
    setPendingDeepDives(next)
  }, [])

  const isDeepDivePending = useCallback(
    (assistantTimestamp?: string): boolean => {
      const chatId = currentChatIdRef.current
      if (!chatId || !assistantTimestamp) return false
      return pendingDeepDives.has(`${chatId}::${assistantTimestamp}`)
    },
    [pendingDeepDives]
  )

  // CEO-1: latest progress line per in-flight dive (same key scheme as pendingDeepDives).
  const [deepDiveProgress, setDeepDiveProgress] = useState<Map<string, string>>(
    () => new Map()
  )

  // 08-24: per-message dive failure (compose flake and friends) — the dive fails cleanly
  // now instead of rendering a broken card dump, so the UI needs a try-again message.
  // Cleared when a new request for the same message starts.
  const [deepDiveErrors, setDeepDiveErrors] = useState<Map<string, string>>(
    () => new Map()
  )

  const deepDiveError = useCallback(
    (assistantTimestamp?: string): string | undefined => {
      const chatId = currentChatIdRef.current
      if (!chatId || !assistantTimestamp) return undefined
      return deepDiveErrors.get(`${chatId}::${assistantTimestamp}`)
    },
    [deepDiveErrors]
  )

  const deepDiveStatus = useCallback(
    (assistantTimestamp?: string): string | undefined => {
      const chatId = currentChatIdRef.current
      if (!chatId || !assistantTimestamp) return undefined
      return deepDiveProgress.get(`${chatId}::${assistantTimestamp}`)
    },
    [deepDiveProgress]
  )

  const requestDeepDive = useCallback(
    async (assistantTimestamp?: string): Promise<boolean> => {
      const chatId = currentChatIdRef.current
      if (!chatId || !assistantTimestamp) return false
      const key = `${chatId}::${assistantTimestamp}`
      if (pendingDeepDivesRef.current.has(key)) return false // already running
      setPendingDeepDive(key, true)
      setDeepDiveErrors((prev) => {
        if (!prev.has(key)) return prev
        const next = new Map(prev)
        next.delete(key)
        return next
      })
      try {
        const deepDive = await fetchDeepDive(chatId, assistantTimestamp, (p) => {
          setDeepDiveProgress((prev) => {
            const next = new Map(prev)
            next.set(key, `${p.text}… (step ${p.step} of ${p.total})`)
            return next
          })
        })
        if (!deepDive) return false
        // Apply only if that chat is still on screen; otherwise the server has
        // persisted it and the next visit (which refetches) will show the bubble.
        if (currentChatIdRef.current === chatId) {
          updateDbMessages((prev) =>
            (prev ?? []).map((m) =>
              m.role === "assistant" && m.timestamp === assistantTimestamp
                ? { ...m, deep_dive: deepDive }
                : m
            )
          )
        }
        return true
      } catch (e) {
        void e // the specific cause doesn't change the remedy — the user just retries
        setDeepDiveErrors((prev) => {
          const next = new Map(prev)
          next.set(key, "The deep dive couldn't be generated. Please try again.")
          return next
        })
        return false
      } finally {
        setPendingDeepDive(key, false)
        setDeepDiveProgress((prev) => {
          const next = new Map(prev)
          next.delete(key)
          return next
        })
      }
    },
    [setPendingDeepDive, updateDbMessages]
  )

  const setChatId = useCallback(
    (id: string) => {
      const cur = inflightRef.current
      if (cur && streamingRef.current) {
        // Backend assigned this turn its real id: re-key the session, and
        // follow it on screen only if the user is still viewing it (so an
        // arrived-late id can't yank them off another chat).
        const wasViewing = sessionOnScreen(cur)
        const wasNewChat = cur.chatId === null
        if (cur.chatId !== id) writeInflight({ ...cur, chatId: id })
        if (wasViewing) updateCurrentChatId(id)
        if (wasNewChat) {
          // The chat now exists server-side. Promote it from the single pending
          // placeholder into the real sidebar list, so it survives navigating
          // away, opening another new chat, or finalizing — none of which the
          // single placeholder slot could withstand. loadHistory reconciles it
          // with the server copy (same id) once the stream completes.
          optimisticChatIdsRef.current.add(id)
          setChats((prev) =>
            prev.some((c) => String(c.chat_id) === id)
              ? prev
              : sortChatsNewestFirst([
                  {
                    chat_id: id,
                    title: cur.title || "Untitled",
                    updated_at: `ACTIVE#${new Date().toISOString()}`,
                    user_id: "",
                  },
                  ...prev,
                ])
          )
          // The placeholder's job is done — the chat is a real list entry now.
          setPendingNewChat((prev) => (prev && !prev.chat_id ? null : prev))
        }
      } else {
        updateCurrentChatId(id)
      }
    },
    [sessionOnScreen, writeInflight, updateCurrentChatId]
  )

  const syncChatJurisdiction = useCallback(
    (jurisdiction: DttJurisdiction | null) => {
      // Only adjust scope if the just-sent turn's chat is the one on screen.
      const cur = inflightRef.current
      if (cur && !sessionOnScreen(cur)) return
      setChatFromJurisdiction(jurisdiction)
    },
    [sessionOnScreen]
  )

  const setPendingNewChatTitle = useCallback((title: string) => {
    // Create the placeholder if none exists (e.g. sending right after deleting the
    // current chat) so the new chat shows in the sidebar immediately on send.
    setPendingNewChat((prev) => (prev ? { ...prev, title } : { title }))
  }, [])

  const setPendingChatId = useCallback((id: string) => {
    setPendingNewChat((prev) => (prev ? { ...prev, chat_id: id } : null))
  }, [])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const value: ChatHistoryContextValue = {
    chats,
    pendingNewChat,
    currentChatId,
    messages,
    isLoading,
    error,
    loadHistory,
    selectChat,
    selectPendingChat,
    createNewChat,
    deleteChat,
    addUserMessage,
    startAssistantMessage,
    appendAssistantToken,
    appendAssistantThinking,
    setAssistantStatus,
    finalizeAssistantMessage,
    cancelAssistantMessage,
    addAssistantMessage,
    submitFeedback,
    requestDeepDive,
    isDeepDivePending,
    deepDiveStatus,
    deepDiveError,
    setChatId,
    setPendingNewChatTitle,
    setPendingChatId,
    newChatVersion,
    chatFromJurisdiction,
    chatSelectionVersion,
    syncChatJurisdiction,
  }

  return (
    <ChatHistoryContext.Provider value={value}>
      {children}
    </ChatHistoryContext.Provider>
  )
}

export function useChatHistory(): ChatHistoryContextValue {
  const ctx = useContext(ChatHistoryContext)
  if (!ctx) {
    throw new Error("useChatHistory must be used within a ChatHistoryProvider")
  }
  return ctx
}
